<script>
import { Search as SearchIcon } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import DialogWrapper from "@/shared/ui/custom/components/DialogWrapper.vue";
import dateFormat from "@/shared/lib/date-format";
import {
  getExecInscriptionList,
  updateExecInscriptionNote,
  updateExecInscriptionDescription,
  changeStatusExecInscription,
} from "@/entities/exec-inscription";
import CreateAction from "@/features/create-action/ui/CreateAction.vue";
import { getReferenceListByCodes } from "@/entities/directory";
import {
  StatusCode,
  ensureStatusDictionary,
  getStatusDictionary,
  getStatusLabel as getStatusLabelFromDict,
} from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { CATEGORY_CODES } from "@/shared/lib/constants";

import Button from "@/shared/ui/button/Button.vue";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import ExecutiveInscriptionFilter from "./components/ExecutiveInscriptionFilter.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

export default {
  name: "ExecutiveInscriptionList",
  components: {
    CreateAction,
    TableFooterWrapper,
    DialogWrapper,
    SearchIcon,
    Input,
    Button,
    TableRowActions,
    ExecutiveInscriptionFilter,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    StatusDot,
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,

      filters: {
        q: "",
        page: 1,
        size: 20,
        number: "",
        notary_uuid: "",
        notary_identifier: "",
        identifier: "",
        status__in: [],
        order_by: "",
      },

      showFilters: false,
      pageSizes: [1, 5, 10, 20],
      showCreateExecInscription: false,

      references: [],

      showNoteDialog: false,
      currentNote: "",
      currentItemId: null,

      showDescriptionDialog: false,
      currentDescription: "",

      execInscriptionCategoryCodes: [CATEGORY_CODES.SOVER_EXECUT_NADPIS],
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },

    notaryData() {
      return this.$store.getters.get_notary || {};
    },

    statusOptions() {
      const dict = getStatusDictionary().value;
      return Array.from(dict.values()).map((s) => ({
        value: s.code,
        label: s.label,
      }));
    },
    actions() {
      const list = [];
      // TODO: заменить TDNT на TDEI после backend
      if (this.$store.getters.has_access("TDNT")) {
        list.push({
          label: this.$t("Отменить"),
          type: "danger",
          action: "cancel",
          icon: "huge-danger",
        });
      }
      list.push(
        {
          label: this.$t("Примечание"),
          type: "default",
          action: "note",
          icon: "huge-text-config",
          isLast: true,
        },
        {
          label: this.$t("Краткое содержание"),
          type: "default",
          action: "summary",
          icon: "huge-document-info",
        },
        {
          label: this.$t("Просмотр"),
          type: "default",
          action: "view",
          icon: "huge-eye",
        }
      );
      return list;
    },
    canCreate() {
      // TODO: заменить TCNT на TCEI после backend
      return this.$store.getters.has_access("TCNT");
    },
  },
  watch: {
    filters: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },
  mounted() {
    this.getList();
    ensureStatusDictionary();
  },
  methods: {
    async getList() {
      let params = {
        size: this.filters.size,
        page: this.filters.page,
      };

      if (this.notaryData?.uuid) {
        params["notary_uuid"] = this.notaryData.uuid;
      }
      if (this.filters.q) {
        params.q = this.filters.q.trim().toLowerCase();
      }
      if (this.filters.number) params.number = this.filters.number;
      if (this.filters.notary_identifier)
        params.notary_identifier = this.filters.notary_identifier;
      if (this.filters.identifier) params.identifier = this.filters.identifier;
      if (this.filters.status__in?.length)
        params.status__in = this.filters.status__in.join(",");
      if (this.filters.order_by) params.order_by = this.filters.order_by;
      this.loading = true;

      try {
        let data = await getExecInscriptionList(params);
        if (data?.success) {
          if (data.payload) {
            this.total = data.payload?.total;
            this.filters.size = data.payload?.size;
            this.filters.page = data.payload?.page;
            this.list = data.payload.items || [];
          }
          if (data.payload.items?.length > 0) {
            let participant_type_codes = [];
            this.list.forEach((item) => {
              item.participants?.forEach((participant) => {
                if (participant.participant_type_code) {
                  participant_type_codes.push(
                    participant.participant_type_code
                  );
                }
              });
            });

            let notary_codes = this.list.map((item) => item.template_code);
            let list = [
              ...new Set(
                [...notary_codes, ...participant_type_codes].filter(
                  (code) => code != null
                )
              ),
            ];
            if (list.length > 0) {
              await this.getReferenceListByCodes(list);
            }
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async getReferenceListByCodes(list) {
      this.loading = true;
      try {
        if (!list || !list.length) return;
        let data = await getReferenceListByCodes({ codes: list });
        if (data?.success) {
          if (data.payload.items?.length) {
            this.references = data.payload.items;
          }
        }
      } finally {
        this.loading = false;
      }
    },
    getReferenceTitle(code) {
      if (!code) {
        return "-";
      }
      if (this.references?.length === 0) return code;
      let reference = this.references.find((item) => item.code === code);
      return reference?.name || code;
    },
    handlePageChange(page) {
      this.filters.page = page;
    },
    goToCreate() {
      this.showCreateExecInscription = true;
    },
    dateFormat(date) {
      if (!date) {
        return "-";
      }
      return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
    },
    async handleActions(item) {
      if (item.action === "edit") {
        this.$router.push({
          name: "exec_inscription_edit",
          params: { id: item.uuid },
        });
      } else if (item.action === "view") {
        this.$router.push({
          name: "exec_inscription_view",
          params: { id: item.uuid },
        });
      } else if (item.action === "cancel") {
        await this.cancelInscription(item.uuid);
      } else if (item.action === "summary") {
        this.openNoteDialog(item.uuid);
      } else if (item.action === "note") {
        this.openDescriptionDialog(item.uuid);
      }
    },
    openNoteDialog(id) {
      const foundItem = this.list.find((item) => item.id === id);
      this.currentItemId = id;
      this.currentNote = foundItem?.note || "";
      this.showNoteDialog = true;
    },
    closeNoteDialog() {
      this.showNoteDialog = false;
      this.currentNote = "";
      this.currentItemId = null;
    },
    async saveNote() {
      try {
        const data = await updateExecInscriptionNote(
          this.currentItemId,
          this.currentNote
        );
        if (data?.success) {
          await this.getList();
        }
      } catch (e) {
        console.error("Ошибка сохранения:", e);
      } finally {
        this.closeNoteDialog();
      }
    },
    openDescriptionDialog(id) {
      const foundItem = this.list.find((item) => item.id === id);
      this.currentItemId = id;
      this.currentDescription = foundItem?.description || "";
      this.showDescriptionDialog = true;
    },
    closeDescriptionDialog() {
      this.showDescriptionDialog = false;
      this.currentDescription = "";
      this.currentItemId = null;
    },
    async saveDescription() {
      try {
        const data = await updateExecInscriptionDescription(
          this.currentItemId,
          this.currentDescription
        );
        if (data?.success) {
          await this.getList();
        }
      } catch (e) {
        console.error("Ошибка сохранения:", e);
      } finally {
        this.closeDescriptionDialog();
      }
    },
    async cancelInscription(id) {
      const data = await changeStatusExecInscription(id, {
        status: StatusCode.CANCELLED,
      });
      if (data?.success || data?.ok) {
        await this.getList();
      }
    },
    handleRowClick(row) {
      const isReadOnly =
        row.status === StatusCode.COMPLETED ||
        row.status === StatusCode.CANCELLED ||
        row.status === StatusCode.REJECTED;

      this.$router.push({
        name: isReadOnly ? "exec_inscription_view" : "exec_inscription_edit",
        params: { id: row.id },
      });
    },
    search() {
      this.filters.page = 1;
    },
    getActions(row) {
      const actions = [...this.actions];
      if (row.status === StatusCode.PROJECT) {
        // TODO: заменить UDNT на TUEI после backend
        if (this.$store.getters.has_access("UDNT")) {
          actions.unshift({
            label: this.$t("Редактировать"),
            type: "default",
            action: "edit",
            icon: "huge-pen2",
          });
        }
      }
      return actions;
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    resetFilters() {
      this.filters.number = "";
      this.filters.notary_uuid = "";
      this.filters.notary_identifier = "";
      this.filters.identifier = "";
      this.filters.status__in = [];
      this.filters.order_by = "";
      this.filters.page = 1;
    },
    getStatusLabel(item) {
      return (
        getStatusLabelFromDict(item.status) ||
        item.status?.label ||
        item.status?.name ||
        item.status ||
        "-"
      );
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div
        v-if="routeName === 'exec_inscription_list'"
        class="flex flex-col gap-4"
      >
        <div class="flex justify-between gap-5">
          <div class="flex items-center gap-3">
            <div class="relative w-80">
              <SearchIcon
                class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
              />
              <Input
                v-model="filters.q"
                :placeholder="$t('Поиск по реестру')"
                class="pl-9"
                @input="search"
              />
            </div>
            <Button variant="outline" @click="toggleFilters" size="icon">
              <svg-icon
                name="huge-filter"
                class="w-5 h-5 text-muted-foreground"
              />
            </Button>
          </div>
          <Button v-if="canCreate" variant="default" @click="goToCreate">
            {{ $t("Создать исп. надпись") }} +
          </Button>
        </div>

        <ExecutiveInscriptionFilter
          v-if="showFilters"
          v-model="filters"
          :statusOptions="statusOptions"
          @change="search"
          @reset="resetFilters"
        />
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :loading="loading" :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="w-[68px]"></TableHead>
                <TableHead class="w-[70px]">{{ $t("ID") }}</TableHead>
                <TableHead class="min-w-[180px]">
                  {{ $t("№ исполнительной надписи") }}
                </TableHead>
                <TableHead class="min-w-[300px]">
                  {{ $t("Вид действия") }}
                </TableHead>
                <TableHead class="min-w-[330px]">
                  {{ $t("Участники") }}
                </TableHead>
                <TableHead class="min-w-[200px]">
                  {{ $t("Дата создания") }}
                </TableHead>
                <TableHead class="min-w-[200px]">
                  {{ $t("Дата регистрации") }}
                </TableHead>
                <TableHead class="min-w-[160px]">{{ $t("Статус") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="9">
                {{ $t("Нет данных") }}
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.id"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell>
                  <TableRowActions
                    :actions="getActions(row)"
                    align="end"
                    @action="
                      (item) =>
                        handleActions({
                          action: item.action,
                          uuid: row.id,
                          rowData: row,
                        })
                    "
                  />
                </TableCell>
                <TableCell>{{ row.id }}</TableCell>
                <TableCell>{{ row?.number }}</TableCell>
                <TableCell>
                  {{ getReferenceTitle(row.template_code) }}
                </TableCell>
                <TableCell>
                  <div
                    v-if="row.participants?.length"
                    class="flex flex-col gap-3"
                  >
                    <div
                      v-for="item in row.participants"
                      :key="item.id"
                      class="flex flex-col gap-1"
                    >
                      <div
                        v-if="item?.participant_type_code"
                        class="text-muted-foreground"
                      >
                        {{ getReferenceTitle(item.participant_type_code) }}
                      </div>
                      <div class="text-foreground text-sm font-medium">
                        <template v-if="item?.first_name">
                          {{
                            item.first_name +
                            " " +
                            item?.last_name +
                            " " +
                            (item?.middle_name ? item.middle_name : "")
                          }}
                        </template>
                        <template v-if="item?.params?.company_name">
                          {{ item.params.company_name }}
                        </template>
                      </div>
                      <div v-if="item?.iin">
                        {{ item.iin }}
                      </div>
                    </div>
                  </div>
                  <div v-else>-</div>
                </TableCell>
                <TableCell>{{ dateFormat(row?.created_at) }}</TableCell>
                <TableCell>{{ dateFormat(row?.registration_date) }}</TableCell>
                <TableCell>
                  <div class="flex items-center gap-2">
                    <StatusDot :code="row.status" />
                    {{ getStatusLabel(row) }}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          v-if="total > filters.size"
          :total="total"
          v-model:pageSize="filters.size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.size"
        >
          <template #pagination>
            <Pagination
              :total="total"
              :items-per-page="filters.size"
              :page="filters.page"
              :sibling-count="1"
              :disabled="loading"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>
    </div>
    <router-view></router-view>
    <CreateAction
      v-if="showCreateExecInscription"
      key="exec-inscription-action"
      :title="$t('Создание исполнительной надписи')"
      :predefined-categories="execInscriptionCategoryCodes"
      route-name="exec_inscription_new"
      @close="showCreateExecInscription = false"
    />

    <DialogWrapper
      v-model="showNoteDialog"
      v-model:content="currentNote"
      :title="$t('Краткое содержание')"
      @close="closeNoteDialog"
      @save="saveNote"
    />

    <DialogWrapper
      v-model="showDescriptionDialog"
      v-model:content="currentDescription"
      :title="$t('Примечание')"
      @close="closeDescriptionDialog"
      @save="saveDescription"
    />
  </div>
</template>

<style scoped lang="scss">
.filter-icon {
  @apply p-2 rounded-md border border-solid border-border bg-card cursor-pointer flex items-center justify-center;
  &:hover {
    @apply bg-muted;
  }
}
</style>
